import { DictionaryItem } from '../../typings/common';
import { GroupId } from '../../typings/groups';
import { AdditionalTargetingField } from '../../typings/internalGroups';
import {
    ExtendedTagValue,
    TagCloudTargeting,
    TagEditorProps,
    TagState,
    TagValue,
    TargetingTagType,
    TargetingTagValue
} from '../../typings/tagCloudTargeting';

export const GREATER_THAN_OR_EQUAL_TO_OPERATOR = '\u2265';
export const LESS_THAN_OR_EQUAL_TO_OPERATOR = '\u2264';
export const EQUALS_OPERATOR = '=';

const EMPTY_VALUE_LENGTH = 0;
const VALUE_WITHOUT_VERSION_LENGTH = 1;
const INCOMPLETE_VALUE_WITH_SINGLE_VERSION_LENGTH = 2;
const VALUE_WITH_SINGLE_VERSION_LENGTH = 3;
const INCOMPLETE_VALUE_WITH_DOUBLE_VERSION_LENGTH = 4;
const VALUE_WITH_DOUBLE_VERSION_LENGTH = 5;

const isEmpty = (value: TagValue): boolean => value.length === EMPTY_VALUE_LENGTH;

const isAllEmpty = (...values: TagValue[]): boolean => values.every(value => isEmpty(value));

const isSingle = (value: string[]): boolean => value.length === VALUE_WITHOUT_VERSION_LENGTH;

const isIncomplete = (value: string[]): boolean =>
    value.length === INCOMPLETE_VALUE_WITH_DOUBLE_VERSION_LENGTH ||
    value.length === INCOMPLETE_VALUE_WITH_SINGLE_VERSION_LENGTH;

const isComplete = (value: string[]): boolean =>
    isSingle(value) ||
    value.length === VALUE_WITH_SINGLE_VERSION_LENGTH ||
    value.length === VALUE_WITH_DOUBLE_VERSION_LENGTH;

enum MathOperatorCode {
    EQUALS = 'equals',
    GREATER = 'greater',
    LESS = 'less'
}

export const getDefaultTagCloudTargetingState = (): TagCloudTargeting => {
    return {
        tags: [],
        isEditorOpen: false,
        savedValue: {
            tags: []
        }
    };
};

export const getTagCloudTargetingId = (key: AdditionalTargetingField, groupId: GroupId): string => {
    return `${key}_${groupId}`;
};

export const isTagIncluded = (tagValue: TargetingTagValue): boolean => tagValue.type === TargetingTagType.INCLUDED;

export const isTagExcluded = (tagValue: TargetingTagValue): boolean => !isTagIncluded(tagValue);

export const mapTagValueToString = (value: TagValue): string => {
    return Array.isArray(value) ? value.join(' ') : value;
};

export const mapTagValueToFirstItem = (value: TagValue): string => {
    if (!Array.isArray(value)) {
        return value.toString();
    }

    if (value.length === 0) {
        return '';
    }

    return value[0].toString();
};

export const mapTag = (tag: TargetingTagValue): TargetingTagValue => {
    if (!Array.isArray(tag.value)) {
        return tag;
    }

    return {
        ...tag,
        value: tag.value.map(val => String(val.trim()))
    };
};

export const addTags = (
    existingTags: TargetingTagValue[],
    addedTags: TargetingTagValue[],
    mapUserInput?: (tag: TagValue) => TagValue,
    canHaveBothTargetingModes?: boolean
): TargetingTagValue[] => {
    const tagType = addedTags[0].type;
    const tagsMap: Record<string, boolean> = existingTags.reduce((acc, tag) => {
        const { value } = tag;

        if (tag.type !== tagType && !canHaveBothTargetingModes) {
            return acc;
        }

        return {
            ...acc,
            [mapTagValueToFirstItem(value).toLowerCase()]: true
        };
    }, {});

    return [
        ...existingTags.filter(tag => canHaveBothTargetingModes || tag.type === tagType),
        ...addedTags
            .map(tag =>
                mapUserInput
                    ? {
                          ...tag,
                          value: mapUserInput(tag.value)
                      }
                    : mapTag(tag)
            )
            .filter(
                tag =>
                    !tagsMap[mapTagValueToFirstItem(tag.value).toLowerCase()] && mapTagValueToString(tag.value) !== ''
            )
    ];
};

export const getComparativeOperatorsDictionary = (): DictionaryItem[] => {
    return [
        {
            val: MathOperatorCode.EQUALS,
            text: EQUALS_OPERATOR
        },
        {
            val: MathOperatorCode.GREATER,
            text: GREATER_THAN_OR_EQUAL_TO_OPERATOR
        },
        {
            val: MathOperatorCode.LESS,
            text: LESS_THAN_OR_EQUAL_TO_OPERATOR
        }
    ];
};

export const extendTagValue = (
    tagValue: TagValue,
    type: TargetingTagType,
    mapUserInput?: (tag: TagValue) => TagValue
): TargetingTagValue => {
    return {
        value: mapUserInput ? mapUserInput(tagValue) : tagValue,
        tagState: TagState.LOADING,
        type
    };
};

export const shrinkTagValue = (tagValue: TargetingTagValue): ExtendedTagValue => {
    return {
        value: tagValue.value,
        renderValue: tagValue.renderValue,
        tagState: tagValue.tagState
    };
};

export const findDictionaryEquivalent = (value: string, dictionaryItems: DictionaryItem[]): string | undefined => {
    const lowerCaseValue = value.trim().toLowerCase();
    const dictionaryItem = dictionaryItems.find(item => item.text.toLowerCase() === lowerCaseValue);

    if (!dictionaryItem) {
        return undefined;
    }

    return dictionaryItem.text;
};

export const onTagEditEnd = (
    value: string,
    props: TagEditorProps,
    isTriggeredByBlur: boolean,
    setValue: (value: string) => void
): void => {
    const { onSubmit, onCancel, onDelete, onBlur, index, suggestItems } = props;

    if (value.length === 0 && onDelete && index !== undefined) {
        onDelete(index);
    } else {
        const lowerCaseValue = value.trim().toLowerCase();
        let isValid = true;

        if (suggestItems) {
            isValid = suggestItems.some(item => item.text.toLowerCase() === lowerCaseValue);
        }

        if (isValid) {
            onSubmit([value], isTriggeredByBlur);
            setValue('');
        } else {
            onCancel(isTriggeredByBlur);
        }
    }

    if (onBlur) {
        onBlur();
    }
};

export const onVersionedTagEditEnd = (
    cachedValue: string[],
    inputValue: string,
    props: TagEditorProps,
    onEnd: () => void,
    isBlurred: boolean = false
): void => {
    const { onSubmit, onCancel, onDelete, index, suggestItems } = props;
    // Если значение пустое и мы находимся в режиме редактирования
    // -> Удаляем текущий тег
    if (isAllEmpty(cachedValue, inputValue) && onDelete) {
        onDelete(index);
        return onEnd();
    }

    // Если значение может быть сохранено в текущем виде
    // (либо не выбраны версии, либо закончен ввод выбранных версий)
    // -> Сохраняем
    if (isComplete(cachedValue)) {
        onSubmit(cachedValue, isBlurred);
        return onEnd();
    }

    // Если ничего не выбрано и не введено
    // -> Отменяем редактирование
    if (isAllEmpty(cachedValue, inputValue)) {
        onCancel(isBlurred);
        return onEnd();
    }

    // Если ничего не выбрано и введён текст
    // -> Проверяем, есть ли такой текст в справочнике
    //    Если да, то сохраняем, если нет, то отменяем
    if (isEmpty(cachedValue) && !isEmpty(inputValue)) {
        const dictionaryItem = suggestItems ? findDictionaryEquivalent(inputValue.trim(), suggestItems) : undefined;

        if (!dictionaryItem) {
            onCancel(isBlurred);
        } else {
            onSubmit([dictionaryItem], isBlurred);
        }
        return onEnd();
    }

    // Если выбор версии или объекта из справочника не закончены,
    // но в поле ввода есть введённое значение
    // -> Пытаемся сохранить
    if (isIncomplete(cachedValue) && !isEmpty(inputValue)) {
        onSubmit([...cachedValue, inputValue], isBlurred);
        return onEnd();
    }

    // Если выбор версии не закончен и поле ввода пустое
    // -> Сохраняем последнее валидное значение
    if (isIncomplete(cachedValue) && isEmpty(inputValue)) {
        const lastCachedValue = [...cachedValue];
        lastCachedValue.pop();
        onSubmit(lastCachedValue, isBlurred);
        return onEnd();
    }
};
